/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, X, Mic, Hand, Eye, Map, Bell,
  Camera, ZoomIn, Filter, Video, Star, Sparkles, Volume2,
  CheckCircle, Rocket
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Guided Tour / Onboarding Component for Skybot
 * Walks new users through all features with interactive highlights
 */

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Skybot!',
    description: 'Your AI-powered sky anomaly detection companion. Let me show you around!',
    icon: Rocket,
    color: '#00f2ea',
    target: null, // No specific element
    position: 'center'
  },
  {
    id: 'camera',
    title: 'Live Camera View',
    description: 'Point your camera at the sky to detect anomalies, UFOs, and celestial objects in real-time.',
    icon: Camera,
    color: '#00f2ea',
    target: '[data-testid="camera-view"]',
    position: 'bottom'
  },
  {
    id: 'zoom',
    title: 'X-Zoom',
    description: 'Use pinch gestures or the zoom controls to magnify with crystal-clear quality using your device\'s native camera.',
    icon: ZoomIn,
    color: '#ffd700',
    target: '[data-testid="zoom-slider"]',
    position: 'left'
  },
  {
    id: 'filters',
    title: 'AI-Powered Filters',
    description: 'Apply Solar Shield to view the sun safely, Night Vision for low light, Thermal for heat signatures, and more!',
    icon: Filter,
    color: '#a855f7',
    target: '[data-testid="filters-panel"]',
    position: 'left'
  },
  {
    id: 'voice',
    title: 'Voice Commands',
    description: 'Say "Capture", "Zoom 50x", "Solar filter", "Analyze" or "Help" for hands-free control!',
    icon: Mic,
    color: '#ff4444',
    target: '[data-testid="voice-command-btn"]',
    position: 'left'
  },
  {
    id: 'gestures',
    title: 'Touch Gestures',
    description: 'Pinch to zoom, double-tap to capture, swipe for filters, long-press to record.',
    icon: Hand,
    color: '#22c55e',
    target: '[data-testid="gesture-toggle-btn"]',
    position: 'left'
  },
  {
    id: 'ar',
    title: 'AR Constellation Overlay',
    description: 'Enable AR mode to see constellation lines, star names, and planets overlaid on your camera!',
    icon: Eye,
    color: '#00f2ea',
    target: '[data-testid="ar-toggle-btn"]',
    position: 'left'
  },
  {
    id: 'starmap',
    title: 'Live Star Map',
    description: 'Interactive sky map showing constellations, planets, moon phase, and celestial positions.',
    icon: Map,
    color: '#00f2ea',
    target: '[data-testid="star-map-btn"]',
    position: 'right'
  },
  {
    id: 'alerts',
    title: 'Sky Alerts',
    description: 'Get notified about ISS passes, meteor showers, eclipses, and planetary alignments!',
    icon: Bell,
    color: '#ffd700',
    target: '[data-testid="night-alerts-btn"]',
    position: 'right'
  },
  {
    id: 'record',
    title: 'Video & Timelapse',
    description: 'Record in Ultra HD (8 Mbps) or capture stunning timelapses of the night sky.',
    icon: Video,
    color: '#ff6b4a',
    target: '[data-testid="record-btn"]',
    position: 'bottom'
  },
  {
    id: 'analyze',
    title: 'AI Analysis (GPT-5.2)',
    description: 'Capture an image and let our AI identify anomalies, aircraft, satellites, and celestial objects.',
    icon: Sparkles,
    color: '#ffd700',
    target: '[data-testid="analyze-btn"]',
    position: 'top'
  },
  {
    id: 'complete',
    title: "You're Ready!",
    description: 'Start scanning the sky for anomalies. The truth is up there!',
    icon: CheckCircle,
    color: '#22c55e',
    target: null,
    position: 'center'
  }
];

// Spotlight overlay component
const Spotlight = ({ target, padding = 12 }) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }

    const element = document.querySelector(target);
    if (element) {
      const updateRect = () => {
        const r = element.getBoundingClientRect();
        setRect({
          top: r.top - padding,
          left: r.left - padding,
          width: r.width + padding * 2,
          height: r.height + padding * 2
        });
      };
      
      updateRect();
      window.addEventListener('resize', updateRect);
      window.addEventListener('scroll', updateRect);
      
      return () => {
        window.removeEventListener('resize', updateRect);
        window.removeEventListener('scroll', updateRect);
      };
    }
  }, [target, padding]);

  if (!rect) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[998] pointer-events-none"
      style={{
        background: `radial-gradient(circle at ${rect.left + rect.width/2}px ${rect.top + rect.height/2}px, transparent ${Math.max(rect.width, rect.height)/2 + 20}px, rgba(0,0,0,0.85) ${Math.max(rect.width, rect.height)/2 + 60}px)`
      }}
    >
      {/* Highlight border */}
      <motion.div
        className="absolute rounded-2xl"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          border: '2px solid #00f2ea',
          boxShadow: '0 0 30px rgba(0,242,234,0.5), inset 0 0 30px rgba(0,242,234,0.1)'
        }}
        animate={{
          boxShadow: [
            '0 0 30px rgba(0,242,234,0.5), inset 0 0 30px rgba(0,242,234,0.1)',
            '0 0 50px rgba(0,242,234,0.8), inset 0 0 50px rgba(0,242,234,0.2)',
            '0 0 30px rgba(0,242,234,0.5), inset 0 0 30px rgba(0,242,234,0.1)'
          ]
        }}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
    </motion.div>
  );
};

// Tooltip component
// Always centered (more consistent across devices). Spotlight handles pointing to the UI.
const TourTooltip = ({ step, onNext, onPrev, onSkip, currentStep, totalSteps }) => {
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 pointer-events-none">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="w-full max-w-sm p-4 sm:p-5 rounded-2xl pointer-events-auto"
        style={{
          background: 'linear-gradient(180deg, rgba(10,16,32,0.98) 0%, rgba(2,4,10,0.98) 100%)',
          border: `2px solid ${step.color}40`,
          boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 40px ${step.color}20`
        }}
      >
        {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div 
          className="p-2 rounded-xl"
          style={{ background: `${step.color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color: step.color }} />
        </div>
        <div className="flex-1">
          <h3 className="font-unbounded font-bold text-lg text-white">{step.title}</h3>
        </div>
        <button
          onClick={onSkip}
          className="p-1 rounded-lg hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      {/* Description */}
      <p className="text-sm text-slate-300 leading-relaxed mb-4">
        {step.description}
      </p>

      {/* Progress */}
      <div className="flex items-center gap-1 mb-4">
        {TOUR_STEPS.map((_, i) => (
          <div
            key={i}
            className="h-1 flex-1 rounded-full transition-all"
            style={{
              background: i <= currentStep ? step.color : 'rgba(255,255,255,0.1)'
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onPrev}
          disabled={currentStep === 0}
          className="flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10"
          style={{ color: 'rgba(255,255,255,0.7)' }}
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        <span className="text-xs text-slate-500 font-mono">
          {currentStep + 1} / {totalSteps}
        </span>

        {currentStep < totalSteps - 1 ? (
          <button
            onClick={onNext}
            className="flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-bold transition-all"
            style={{
              background: `linear-gradient(135deg, ${step.color}, ${step.color}aa)`,
              color: step.color === '#ffd700' ? '#000' : '#fff'
            }}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={onSkip}
            className="flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-bold transition-all"
            style={{
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              color: '#fff'
            }}
          >
            Start Exploring!
            <Rocket className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Skip link */}
      <button
        onClick={onSkip}
        className="w-full text-center text-xs text-slate-600 mt-3 hover:text-slate-400 transition-colors"
      >
        Skip tour
      </button>
      </motion.div>
    </div>
  );
};

// Main Tour Component
const GuidedTour = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = TOUR_STEPS[currentStep];

  // Swipe navigation for mobile
  useEffect(() => {
    if (!isOpen) return;

    let startX = null;
    const minSwipe = 50;

    const onTouchStart = (e) => {
      startX = e.touches?.[0]?.clientX ?? null;
    };

    const onTouchEnd = (e) => {
      if (startX === null) return;
      const endX = e.changedTouches?.[0]?.clientX ?? null;
      if (endX === null) return;

      const dx = endX - startX;
      if (Math.abs(dx) < minSwipe) return;

      if (dx < 0) {
        // swipe left -> next
        if (currentStep < TOUR_STEPS.length - 1) setCurrentStep((s) => s + 1);
      } else {
        // swipe right -> prev
        if (currentStep > 0) setCurrentStep((s) => s - 1);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [isOpen, currentStep]);

  // Update target element rect
  useEffect(() => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(step.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });

      // Scroll element into view if needed
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  const handleNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleClose = useCallback(() => {
    setCurrentStep(0);
    onClose();
    // Save that user has seen the tour
    localStorage.setItem('skybot_tour_completed', 'true');
  }, [onClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleNext, handlePrev, handleClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {/* Spotlight overlay */}
      <Spotlight target={step?.target} />

      {/* Dark overlay for center positioned tooltips */}
      {step?.position === 'center' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[998] bg-black/85 pointer-events-none"
        />
      )}

      {/* Tooltip */}
      <TourTooltip
        step={step}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleClose}
        currentStep={currentStep}
        totalSteps={TOUR_STEPS.length}
      />
    </AnimatePresence>
  );
};

// Hook to check if tour should be shown
export const useTourState = () => {
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    // Check if user has completed the tour
    const completed = localStorage.getItem('skybot_tour_completed');
    if (!completed) {
      // Show tour after a short delay for first-time users
      const timer = setTimeout(() => {
        setShowTour(true);
      }, 900);
      return () => clearTimeout(timer);
    }
  }, []);

  const startTour = useCallback(() => {
    setShowTour(true);
  }, []);

  const endTour = useCallback(() => {
    setShowTour(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem('skybot_tour_completed');
    setShowTour(true);
  }, []);

  return { showTour, startTour, endTour, resetTour };
};

export default GuidedTour;
